import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import Ornament from "@/components/Ornament";
import AvailabilityCalendar from "@/components/AvailabilityCalendar";
import { api } from "@/lib/config";
import { formatUKDate } from "@/lib/date";
import { TESTIMONIALS } from "@/data/testimonials";
import { ArrowRight, Music } from "lucide-react";

export default function BlogIndex() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/blog")
      .then((r) => setPosts(r.data || []))
      .finally(() => setLoading(false));
  }, []);

  const upcoming = posts.filter((p) => p.upcoming);
  const past = posts.filter((p) => !p.upcoming);

  return (
    <>
      <Navigation />
      <section className="max-w-6xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-8 text-center">
        <div className="eyebrow">The House Diary</div>

        <div className="mt-12 mb-4 border-y border-ink/15 py-12">
          <AvailabilityCalendar />
        </div>

        <h1 className="press-title text-5xl md:text-7xl text-ink mt-12">Marginalia</h1>
        <p className="font-display italic text-ink/70 text-xl md:text-2xl mt-4 max-w-2xl mx-auto">
          Things that have happened at the house, and things that will.
        </p>
        <Ornament />
      </section>

      <section className="max-w-6xl mx-auto px-6 md:px-12 pb-24" data-testid="diary-page">
        {loading && <div className="font-serif text-ink/60 text-center py-12">Loading...</div>}

        {!loading && posts.length === 0 && (
          <div className="text-center py-16 border-y border-ink/20" data-testid="journal-empty">
            <p className="font-display italic text-2xl text-ink/70">
              The diary is being written.
            </p>
            <p className="font-serif text-ink/60 mt-3">
              Check back soon for stories from the house.
            </p>
          </div>
        )}

        {upcoming.length > 0 && (
          <div className="mb-16">
            <div className="eyebrow mb-6">Upcoming</div>
            <div className="grid md:grid-cols-2 gap-10">
              {upcoming.map((p) => (
                <PostCard key={p.id} post={p} />
              ))}
            </div>
          </div>
        )}

        {past.length > 0 && (
        <div className="border-t border-ink/20 pt-10">
          <div className="eyebrow mb-6">Past entries</div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
            {past.map((p) => <PostCard key={p.id} post={p} />)}
          </div>
        </div>
        )}
      </section>

      <section className="max-w-6xl mx-auto px-6 md:px-12 pb-24" data-testid="diary-reviews">
        <div className="border-t border-ink/20 pt-12">
          <div className="grid md:grid-cols-2 gap-10 md:gap-16">
            {TESTIMONIALS.map((t, i) => (
              <figure key={i} className="border-t border-ink/25 pt-8" data-testid={`diary-testimonial-${i}`}>
                <Music className="w-5 h-5 text-ink/50 mb-5" strokeWidth={1.3} />
                <blockquote className="font-display text-lg md:text-xl leading-relaxed text-ink/90">
                  &ldquo;{t.quote}&rdquo;
                </blockquote>
                <figcaption className="mt-6 font-sans text-[0.72rem] uppercase tracking-[0.22em] text-ink/60">
                  {t.by}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
}

function PostCard({ post }) {
  return (
    <Link to={`/diary/${post.slug}`} className="group block" data-testid={`post-card-${post.slug}`}>
      <div className="overflow-hidden">
        {post.cover_video ? (
          <video
            src={post.cover_video}
            className="w-full aspect-[4/3] object-cover transition-transform duration-700 group-hover:scale-[1.03]"
            muted
            loop
            playsInline
            autoPlay
            preload="metadata"
          />
        ) : (
          <img
            src={post.cover_image}
            alt={post.title}
            className="w-full aspect-[4/3] object-cover transition-transform duration-700 group-hover:scale-[1.03]"
          />
        )}
      </div>
      <div className="pt-4">
        <div className="font-sans text-[0.68rem] uppercase tracking-[0.22em] text-ink/60 flex gap-3">
          <span>{formatUKDate(post.published_at)}</span>
        </div>
        <h3 className="font-display text-2xl md:text-3xl text-ink mt-2 leading-tight group-hover:text-ink/70 transition-colors">
          {post.title}
        </h3>
        <div className="mt-4 inline-flex items-center gap-2 font-sans text-[0.7rem] uppercase tracking-[0.22em] text-ink/70">
          Read on <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
        </div>
      </div>
    </Link>
  );
}
